import { useCallback, useMemo, useState } from 'react';

import { SearchBar } from '@/components/chariot-ui';
import { FormGroup } from '@/components/form/FormGroup';
import { MultiSelectTable } from '@/components/table/MultiSelectTable';
import { Capability } from '@/hooks/useCapabilities';
import { useMy } from '@/hooks/useMy';
import {
  buildTargetFilters,
  getCapabilityMatchConfig,
  getMatchDescription,
} from '@/sections/attacks/capabilities/utils/target-match-conditions';
import { AssetStatus, MyResource, NodeLabel } from '@/types';
import { capitalize } from '@/utils/lodash.util';

import {
  type RepositoryMode,
  RepositoryModeToggle,
} from './RepositoryModeToggle';
import { getTargetColumns, type TargetItem } from './targetColumns';
import { ZipTargetUploader } from './ZipTargetUploader';

// Re-export so callers who imported from here continue to work
export type { TargetItem };

/**
 * Map target type to the correct NodeLabel for queries.
 * Handles special cases where the label differs from simple capitalization.
 */
function getTargetNodeLabel(targetType?: string): NodeLabel {
  if (!targetType) return 'Asset';

  switch (targetType) {
    case 'addomain':
      return 'ADDomain';
    case 'webapplication':
      return 'WebApplication';
    case 'awsresource':
      return 'AWSResource';
    case 'gcpresource':
      return 'GCPResource';
    case 'azureresource':
      return 'AzureResource';
    default:
      return capitalize(targetType) as NodeLabel;
  }
}

/**
 * Props for the TargetSelector component.
 *
 * Used in CapabilityDrawer to select targets (assets, ports, repositories, etc.)
 * for capability execution. The component filters available targets based on
 * capability-specific match conditions.
 */
interface TargetSelectorProps {
  /**
   * Currently selected target keys.
   *
   * Target keys follow the format: `#{type}#{identifier}`
   *
   * @example
   * // Port key
   * '#port#192.168.1.1:80#tcp'
   *
   * @example
   * // Asset key
   * '#asset#example.com'
   */
  selectedTargets: string[];

  /**
   * Callback invoked when the selection changes.
   *
   * @param targets - Updated array of selected target keys
   */
  onTargetsChange: (targets: string[]) => void;

  /**
   * The capability being configured.
   *
   * Determines:
   * - Target type (port, asset, repository, etc.)
   * - Match conditions for filtering available targets
   * - Display labels and empty state messages
   *
   * @optional May be undefined during initial render
   */
  capability?: Capability;

  /**
   * Function that returns a human-readable label for the target type.
   *
   * Used for:
   * - Form label: "Select {label}"
   * - Search placeholder: "Search {label}..."
   * - Selection count: "X {label} selected"
   *
   * @returns Pluralized, capitalized label (e.g., "Assets", "Ports", "Repositories")
   *
   * @example
   * getTargetLabel() // Returns "Ports" for port-based capability
   * getTargetLabel() // Returns "AD Domains" for addomain capability
   */
  getTargetLabel: () => string;
}

/**
 * Convert a plural target label (e.g. "Repositories", "AD Domains")
 * to singular when there's exactly one item. Strips a trailing "s",
 * with a special case for the multi-word "AD Domains" form.
 */
function pluralizeTargetLabel(plural: string, count: number): string {
  const lower = plural.toLowerCase();
  if (count !== 1) return lower;
  if (lower === 'ad domains') return 'ad domain';
  if (lower.endsWith('ies')) return `${lower.slice(0, -3)}y`;
  if (lower.endsWith('s')) return lower.slice(0, -1);
  return lower;
}

/**
 * TargetSelector — Searchable inline multi-select table for capability targets.
 *
 * Replaces the Dropdown pattern with a SearchBar + MultiSelectTable.
 * Supports infinite scroll and pin-selected sorting.
 */
export function TargetSelector({
  selectedTargets,
  onTargetsChange,
  capability,
  getTargetLabel,
}: TargetSelectorProps) {
  const [searchValue, setSearchValue] = useState('');

  const targetType = capability?.target?.[0]?.toLowerCase();

  // Repository-only mode state. Resets automatically when capability changes
  // because CapabilityDrawer remounts via key={capabilityDrawerKey}.
  //
  // We deliberately do NOT cache an "uploaded asset key" in local state.
  // The single source of truth for what's selected is `selectedTargets`;
  // switching modes always clears the selection and the user re-uploads
  // if they need a new file-type repo. (Previous design cached a
  // dropped-on-the-floor key and drifted out of sync with the parent.)
  const [repoMode, setRepoMode] = useState<RepositoryMode>('search');

  const isRepositoryTarget = targetType === 'repository';
  const showSearchUi = !isRepositoryTarget || repoMode === 'search';

  const matchConfig = getCapabilityMatchConfig(capability?.name);
  const capabilityFilters = useMemo(() => {
    if (!matchConfig) return [];
    if (matchConfig.target !== targetType) return [];
    return buildTargetFilters(matchConfig);
  }, [matchConfig, targetType]);

  const {
    data: targets,
    status,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMy(
    {
      resource:
        (targetType === 'domain'
          ? 'asset'
          : (targetType as keyof MyResource)) || 'asset',
      query: {
        node: {
          labels: [getTargetNodeLabel(targetType)],
          filters: [
            {
              field: 'status',
              operator: '=',
              value: AssetStatus.Frozen,
              not: true,
            },
            {
              field: 'status',
              operator: '=',
              value: AssetStatus.Deleted,
              not: true,
            },
            ...(targetType === 'domain'
              ? [
                  {
                    field: '',
                    operator: 'OR' as const,
                    value: [
                      {
                        field: 'class',
                        operator: '=' as const,
                        value: 'domain',
                      },
                      { field: 'class', operator: '=' as const, value: 'tld' },
                    ],
                  },
                ]
              : []),
            ...capabilityFilters,
            ...(searchValue
              ? targetType === 'port'
                ? [
                    {
                      field: '',
                      operator: 'OR' as const,
                      value: [
                        {
                          field: 'key',
                          operator: 'CONTAINS' as const,
                          value: searchValue,
                        },
                        {
                          field: 'service',
                          operator: 'CONTAINS' as const,
                          value: searchValue,
                        },
                        ...(isNaN(Number(searchValue))
                          ? []
                          : [
                              {
                                field: 'port',
                                operator: '=' as const,
                                value: Number(searchValue),
                              },
                            ]),
                      ],
                    },
                  ]
                : [
                    {
                      field: 'key',
                      operator: 'CONTAINS' as const,
                      value: searchValue,
                    },
                  ]
              : []),
          ],
        },
        limit: 200,
      },
    },
    { enabled: !!targetType }
  );

  const filteredTargets = useMemo(() => {
    if (!targets?.length) return [];
    const expectedPrefix = `#${targetType === 'domain' ? 'asset' : targetType}#`;
    return (targets as TargetItem[]).filter(item =>
      item.key.startsWith(expectedPrefix)
    );
  }, [targets, targetType]);

  const selectedSet = useMemo(
    () => new Set(selectedTargets),
    [selectedTargets]
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      onTargetsChange(Array.from(keys));
    },
    [onTargetsChange]
  );

  const columns = useMemo(
    () => getTargetColumns(targetType || 'asset'),
    [targetType]
  );

  const getEmptyStateLabel = (): string => {
    if (status === 'pending') return 'Loading...';

    if (capability?.name) {
      const matchDescription = getMatchDescription(capability.name);
      if (matchDescription) {
        return `No matching targets. This capability requires ${matchDescription}.`;
      }
    }

    return 'No results found';
  };

  return (
    <FormGroup label={`Select ${getTargetLabel()}`} name="target-selector">
      <div className="space-y-2">
        {isRepositoryTarget && (
          <RepositoryModeToggle
            mode={repoMode}
            onModeChange={next => {
              setRepoMode(next);
              // Clear current selection when switching modes. We do not
              // re-apply a previously-uploaded zip; switching is
              // intentionally a fresh-start operation.
              onTargetsChange([]);
            }}
          />
        )}

        {showSearchUi ? (
          <>
            <SearchBar
              size="sm"
              className="h-fit w-full"
              placeholder={`Search ${getTargetLabel()}...`}
              value={searchValue}
              onChange={setSearchValue}
            />
            <MultiSelectTable
              data={filteredTargets}
              columns={columns}
              selectedKeys={selectedSet}
              onSelectionChange={handleSelectionChange}
              isLoading={status === 'pending'}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              maxRows={8}
              emptyMessage={getEmptyStateLabel()}
              pinSelected={!searchValue}
            />
            {selectedTargets.length > 0 && (
              <p className="text-chariot-text-secondary text-sm">
                {selectedTargets.length}{' '}
                {pluralizeTargetLabel(getTargetLabel(), selectedTargets.length)}{' '}
                selected
              </p>
            )}
          </>
        ) : (
          <ZipTargetUploader
            onChange={onTargetsChange}
            label={getTargetLabel()}
          />
        )}
      </div>
    </FormGroup>
  );
}
